import { useEffect } from "react";

const FORCE_EN_KEY = "forceEN";

// use placeholder procotol in order to create URL objects without a base URL on SSR
const PLACEHOLDER_BASE_URL = "thismessage:/";
// part of the placeholder to remove from the final url
const PLACEHOLDER_PROTOCOL = "thismessage:";

/**
 * Converts browser language to a language code that is supported on a ciom url i.e. `/ciom/*`
 * @param {string} browserLanguage Browser language code
 * @returns Language code to use in ciom url
 */
export const mapBrowserLanguageToOticonUrlLanguage = (
  browserLanguage,
  languages,
) => {
  // en is not included, because en is treated as a "default" case to not allow further redirects
  for (const language of languages.filter((_language) => _language !== "en")) {
    if (browserLanguage.startsWith(language)) {
      return language;
    }

    // exceptions
    if (
      language === "no" &&
      ["no", "nb", "nb-no", "nn-no"].includes(browserLanguage.toLowerCase())
    ) {
      return "no";
    }

    if (language === "pt-br" && browserLanguage.toLowerCase() === "pt-br") {
      return "pt-br";
    }
  }

  return "en";
};

/**
 * Replaces the language in a CIOM URL.
 * @param {string} url Relative URL (without host to support SSR)
 * @param {string} language New language code
 * @param {boolean} forceEN Whether or not to force EN after redirecting
 * @returns New relative URL with language replaced
 */
export const replaceOticonUrlLanguage = (url, language, forceEN = false) => {
  const newPathname = url.replace(
    /\/ciom\/[^/]{2,}(\/|$)/,
    `/ciom/${language}$1`,
  );

  const urlObj = new URL(newPathname, PLACEHOLDER_BASE_URL);

  if (forceEN) {
    urlObj.searchParams.set(FORCE_EN_KEY, "true");
  } else {
    urlObj.searchParams.delete(FORCE_EN_KEY);
  }

  return urlObj.toString().replace(PLACEHOLDER_PROTOCOL, "");
};

/**
 * Replaces the language in a Sitecore URL.
 * @param {string} url Relative URL (without host to support SSR)
 * @param {string} language New language code
 * @returns New relative URL with language replaced
 */
export const replaceUrlLanguageForSitecore = (url, language) => {
  const urlObj = new URL(url, PLACEHOLDER_BASE_URL);
  urlObj.searchParams.set("sc_lang", language);
  return urlObj.toString().replace(PLACEHOLDER_PROTOCOL, "");
};

/**
 * If on `/ciom/en` page and browser language is different, attempt to redirect to matching language page
 */
export const useOticonLanguageRedirect = (languages) => {
  // can safely use window.location inside useEffect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (
      // not sitecore editing
      !params.has("sc_mode") &&
      // only on global and en - default language
      window.location.pathname.toLowerCase().startsWith("/ciom/en") &&
      params.get(FORCE_EN_KEY) !== "true" &&
      navigator.language &&
      !navigator.language.startsWith("en")
    ) {
      const newLanguage = mapBrowserLanguageToOticonUrlLanguage(
        navigator.language,
        languages,
      );

      // don't redirect to en - will cause a loop
      // en treated as default if no language match is found
      if (newLanguage !== "en") {
        window.location.replace(
          replaceOticonUrlLanguage(window.location.href, newLanguage),
        );
      }
    }
  }, [languages]);
};
